import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PainChart } from "@/components/portal/PainChart";
import { Loader2, Send, Save, ArrowUp, ArrowDown } from "lucide-react";

const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const CheckinRow = ({ c }) => (
  <li className="flex flex-wrap items-start gap-2 border-b border-slate-100 py-2 text-sm last:border-0" data-testid={`admin-checkin-${c.id}`}>
    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-xs font-bold">{c.pain}/10</span>
    {c.change ? <span className={`inline-flex items-center text-xs font-semibold ${c.change > 0 ? "text-red-600" : "text-emerald-600"}`}>{c.change > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}{Math.abs(c.change)}</span> : null}
    <span className="flex-1 text-slate-600">{c.comment || <i className="text-slate-400">No comment</i>}</span>
    <span className="text-[11px] text-slate-400">{when(c.created_at)} · {c.via}</span>
  </li>
);

export default function PortalPanel({ clinikoId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ frequency: "weekly", allow_anytime: false, enabled: true });
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/portal-admin/${clinikoId}`).then((r) => {
      setData(r.data);
      if (r.data.settings) setForm({ frequency: r.data.settings.frequency, allow_anytime: r.data.settings.allow_anytime, enabled: r.data.settings.enabled });
    }).catch(() => setData({ settings: null, checkins: [] }));
  }, [clinikoId]);
  useEffect(() => { load(); }, [load]);

  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save") { await api.put(`/portal-admin/${clinikoId}`, form); toast.success("Portal settings saved"); }
      else { const r = await api.post(`/portal-admin/${clinikoId}/invite`); toast.success(`Check-in link emailed to ${r.data.to}`); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong — retry"); }
    finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-teal-600" />;
  const s = data.settings;
  return (
    <div className="space-y-4" data-testid="portal-panel">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="font-heading text-sm font-bold text-slate-800">Patient portal access</div>
        <p className="mt-0.5 text-xs text-slate-500">Patients sign in at <span className="font-mono">/portal</span> with their email + a 6-digit code.{s?.email ? ` Email on file: ${s.email}` : ""}</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="portal-enabled-switch" /> Portal on</label>
          <div>
            <div className="mb-1 text-xs font-semibold text-slate-600">Check-in reminders</div>
            <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
              <SelectTrigger className="bg-white" data-testid="portal-frequency-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Every 2 weeks</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="off">Off (no emails)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.allow_anytime} onCheckedChange={(v) => setForm({ ...form, allow_anytime: v })} data-testid="portal-anytime-switch" /> Let them check in anytime</label>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => act("save")} disabled={!!busy} className="bg-teal-600 hover:bg-teal-700" data-testid="portal-save-btn">{busy === "save" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Save</Button>
          <Button size="sm" variant="outline" onClick={() => act("invite")} disabled={!!busy || !s?.enabled} data-testid="portal-invite-btn">{busy === "invite" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 h-3.5 w-3.5" />} Email check-in link now</Button>
          {s?.next_due && s.enabled && <span className="text-xs text-slate-400" data-testid="portal-next-due">Next reminder: {when(s.next_due)}</span>}
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-3 font-heading text-sm font-bold text-slate-800">Pain trend</div>
        <PainChart checkins={data.checkins} height={180} testid="admin-pain-chart" emptyText="No check-ins from this patient yet." />
        {data.checkins.length > 0 && <ul className="mt-4">{[...data.checkins].reverse().map((c) => <CheckinRow key={c.id} c={c} />)}</ul>}
      </div>
    </div>
  );
}
